import React from 'react';
import { PlayIcon, TelevisionIcon } from '@phosphor-icons/react';
import { withBlockExtensions } from '@plone/volto/helpers';
import { MPFBlockCardHeader } from 'volto-ministerio-publico-federal/components/UI';
import { EditItemButton } from 'volto-ministerio-publico-federal/components/View/CapaView/EditItemButton';

import { VideoPrincipal } from './VideoPrincipal';
import {
  contarVideosInvalidos,
  toVideoCards,
  VIDEOS_LATERAIS_MAX,
  VIDEOS_MAX_ITENS,
  VIDEOS_TITULO,
  type VideoCard,
  type VideosData,
} from './helpers';
import { comPrefixo } from 'volto-ministerio-publico-federal/helpers/Url/subpath';

interface ViewProps {
  data: VideosData;
  isEditMode?: boolean;
  /** Presente só no editor: abre o vídeo de índice N na barra lateral. */
  onEditItem?: (index: number) => void;
}

/* `not-prose` desliga a tipografia de texto corrido que a página herda do
   editor (`[class*='__editor-content']`, em `base/_editor-content.scss`): a
   faixa traz a própria tipografia, e sem isto o `prose` recolore e
   redimensiona títulos e links de dentro do cartão. */
const ROOT_CLASS = 'mpf-videos not-prose';

const Aviso: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mpf-videos__aviso">{children}</p>
);

/**
 * Faixa de vídeos do YouTube: um no player e até cinco na lateral.
 *
 * A lateral lista os OUTROS vídeos, nunca o que está em cartaz — clicar em um
 * deles o promove ao player e devolve o anterior à lista. Sem isso a lista
 * teria uma linha morta (a do vídeo que já está sendo exibido) e o editor
 * precisaria contar seis chamadas para ver cinco.
 */
export const MpfVideosBlockView: React.FC<ViewProps> = ({
  data,
  isEditMode,
  onEditItem,
}) => {
  const tituloId = React.useId();
  const cards = React.useMemo(() => toVideoCards(data?.videos), [data?.videos]);
  const [ativo, setAtivo] = React.useState(0);

  // O editor pode remover o vídeo que estava em cartaz enquanto edita.
  const indiceAtivo = ativo < cards.length ? ativo : 0;
  const emCartaz = cards[indiceAtivo];

  const laterais = cards
    .filter((_, index) => index !== indiceAtivo)
    .slice(0, VIDEOS_LATERAIS_MAX);

  const invalidos = contarVideosInvalidos(data?.videos);
  const excedentes = cards.length - VIDEOS_MAX_ITENS;

  const titulo = data?.titulo?.trim() || VIDEOS_TITULO;
  const cabecalho =
    data?.exibirTitulo === false ? null : (
      <MPFBlockCardHeader
        icon={
          <TelevisionIcon
            weight="fill"
            aria-hidden="true"
            className="h-6 w-6 shrink-0 text-teal-600"
          />
        }
        title={titulo}
        titleId={tituloId}
      />
    );

  if (!emCartaz) {
    // No site, faixa sem vídeo utilizável simplesmente não existe.
    if (!isEditMode) return null;

    return (
      <section className={ROOT_CLASS}>
        {cabecalho}
        <Aviso>
          {invalidos > 0
            ? 'Nenhum endereço da lista é um vídeo do YouTube. Cole o endereço da página do vídeo na barra lateral.'
            : 'Escolha os vídeos da faixa na barra lateral.'}
        </Aviso>
      </section>
    );
  }

  return (
    <section
      className={ROOT_CLASS}
      {...(cabecalho
        ? { 'aria-labelledby': tituloId }
        : { 'aria-label': titulo })}
    >
      {cabecalho}

      <div className="mpf-videos__grid">
        <div className="mpf-videos__coluna mpf-videos__coluna--player">
          {onEditItem && (
            <EditItemButton
              label={`Editar vídeo: ${emCartaz.titulo || 'sem título'}`}
              onClick={() => onEditItem(emCartaz.sourceIndex)}
              className="absolute right-2 top-2"
            />
          )}
          {/* `key`: trocar o vídeo em cartaz remonta o player, que é o que
              devolve a fachada e para o áudio do anterior. */}
          <VideoPrincipal
            key={emCartaz.key}
            card={emCartaz}
            isEditMode={isEditMode}
          />
        </div>

        {laterais.length > 0 && (
          <ul className="mpf-videos__lista">
            {laterais.map((card) => (
              <li key={card.key} className="mpf-videos__item">
                <VideoLateral
                  card={card}
                  onSelect={() =>
                    setAtivo(cards.findIndex((c) => c.key === card.key))
                  }
                  onEditItem={onEditItem}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      {isEditMode && invalidos > 0 && (
        <Aviso>
          {invalidos === 1
            ? 'Um item não aparece: o endereço não é de um vídeo do YouTube.'
            : `${invalidos} itens não aparecem: o endereço não é de um vídeo do YouTube.`}
        </Aviso>
      )}

      {isEditMode && excedentes > 0 && (
        <Aviso>
          {excedentes === 1
            ? `O último vídeo não aparece: a faixa exibe ${VIDEOS_MAX_ITENS} (um no player e ${VIDEOS_LATERAIS_MAX} na lateral).`
            : `Os últimos ${excedentes} vídeos não aparecem: a faixa exibe ${VIDEOS_MAX_ITENS} (um no player e ${VIDEOS_LATERAIS_MAX} na lateral).`}
        </Aviso>
      )}
    </section>
  );
};

/**
 * Chamada da lateral: miniatura com selo de play e o título ao lado.
 *
 * O botão é o item inteiro — não há link para lugar nenhum, o clique só troca
 * o vídeo em cartaz, e um `<a>` sem destino seria mentira para quem navega por
 * teclado ou leitor de tela.
 */
const VideoLateral: React.FC<{
  card: VideoCard;
  onSelect: () => void;
  onEditItem?: (index: number) => void;
}> = ({ card, onSelect, onEditItem }) => {
  const [thumbQuebrada, setThumbQuebrada] = React.useState(false);
  const rotulo = card.titulo
    ? `Assistir ao vídeo: ${card.titulo}`
    : 'Assistir ao vídeo';

  return (
    <div className="mpf-videos__item-conteudo">
      <button
        type="button"
        className="mpf-videos__item-botao"
        onClick={onSelect}
        title={rotulo}
      >
        <span className="mpf-videos__item-thumb">
          <img
            src={comPrefixo(thumbQuebrada ? card.thumbFallback : card.thumb)}
            alt=""
            loading="lazy"
            onError={() => setThumbQuebrada(true)}
          />
          <span className="mpf-videos__play" aria-hidden="true">
            <PlayIcon weight="fill" />
          </span>
        </span>
        <span className="mpf-videos__item-titulo">
          {card.titulo || rotulo}
          {!card.titulo && <span className="sr-only">{rotulo}</span>}
        </span>
      </button>

      {onEditItem && (
        <EditItemButton
          label={`Editar vídeo: ${card.titulo || 'sem título'}`}
          onClick={() => onEditItem(card.sourceIndex)}
          className="absolute right-1 top-1"
        />
      )}
    </div>
  );
};

export default withBlockExtensions(MpfVideosBlockView);
